// Audio-reactive terrain analyzer using Web Audio API
// Connects an HTML5 audio element to an AnalyserNode for frequency analysis

let audioContext = null;
let analyser = null;
let sourceNode = null;
let frequencyData = null;
let smoothedEnergy = 0.0;
let enabled = false; // Toggle flag - keeps audio graph alive when false

// Smoothing parameters
const ATTACK_FACTOR = 0.3;  // Fast attack for snappy response
const RELEASE_FACTOR = 0.15; // Faster release for dynamic volume tracking (was 0.06)

// Initialize audio-reactive system with an HTML5 audio element
// Safe to call multiple times - only connects once per audio element
export function initAudioReactive(audioElement) {
  // Guard against double-init (createMediaElementAudioSource throws if called twice)
  if (sourceNode !== null) {
    console.log('Audio reactive already connected, keeping existing graph');
    return;
  }

  if (!audioElement) {
    console.error('No audio element provided');
    return;
  }

  try {
    // Create AudioContext (resume if suspended - browsers require user interaction)
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    console.log('AudioContext created, state:', audioContext.state);
    
    // Create analyser node
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 256; // 128 frequency bins
    analyser.smoothingTimeConstant = 0.8; // Smooth frequency data
    console.log('Analyser created with', analyser.frequencyBinCount, 'frequency bins');
    
    // Connect audio element to analyser
    sourceNode = audioContext.createMediaElementSource(audioElement);
    sourceNode.connect(analyser);
    analyser.connect(audioContext.destination); // Route to speakers
    console.log('Audio graph connected: audio -> analyser -> destination');
    
    // Allocate frequency data array
    frequencyData = new Uint8Array(analyser.frequencyBinCount);
    
    // Resume context if suspended (required after user interaction)
    if (audioContext.state === 'suspended') {
      console.log('AudioContext suspended, resuming...');
      audioContext.resume().then(() => {
        console.log('AudioContext resumed, state:', audioContext.state);
      });
    }
    
    console.log('✓ Audio reactive system initialized successfully');
  } catch (err) {
    console.error('Failed to initialize audio reactive:', err);
    destroyAudioReactive();
    throw err;
  }
}

// Enable or disable reactive terrain (without destroying audio graph)
export function setReactiveEnabled(val) {
  enabled = val;
  console.log('Audio reactive', enabled ? 'enabled' : 'disabled');
  
  // Reset smoothed energy when disabling to start fresh next time
  if (!enabled) {
    smoothedEnergy = 0.0;
  }
}

// Debug counter for periodic logging
let debugCounter = 0;

// Get smoothed volume (0-1) using RMS across all frequency bins
export function getAudioEnergy() {
  if (!enabled || !analyser || !frequencyData) {
    return 0.0;
  }
  
  // Get current frequency data
  analyser.getByteFrequencyData(frequencyData);
  
  // Compute RMS (root mean square) across ALL frequency bins for true volume
  let sumSquares = 0;
  const binCount = frequencyData.length;
  for (let i = 0; i < binCount; i++) {
    const normalized = frequencyData[i] / 255; // Normalize to 0-1
    sumSquares += normalized * normalized;
  }
  
  // RMS and normalize to 0-1
  let rawEnergy = Math.sqrt(sumSquares / binCount);
  
  // Apply power curve to expand small values into perceptible range
  // sqrt expands the lower end: 0.1 -> 0.316, 0.2 -> 0.447, 0.5 -> 0.707
  rawEnergy = Math.sqrt(rawEnergy);
  
  // Apply smoothing: fast attack, faster release (for dynamic volume tracking)
  if (rawEnergy > smoothedEnergy) {
    // Attack: move quickly toward higher energy
    smoothedEnergy += (rawEnergy - smoothedEnergy) * ATTACK_FACTOR;
  } else {
    // Release: move toward lower energy (faster than before for dynamics)
    smoothedEnergy += (rawEnergy - smoothedEnergy) * RELEASE_FACTOR;
  }
  
  // Debug logging every 60 frames (~1 second)
  debugCounter++;
  if (debugCounter % 60 === 0) {
    console.log('Audio volume - raw:', rawEnergy.toFixed(3), 'smoothed:', smoothedEnergy.toFixed(3));
  }
  
  return Math.max(0, Math.min(1, smoothedEnergy));
}

// Check if audio-reactive system is active
export function isAudioReactiveActive() {
  const active = enabled && analyser !== null && sourceNode !== null && audioContext !== null;
  if (enabled && audioContext && audioContext.state === 'suspended') {
    console.warn('AudioContext is suspended - audio may not be analyzed');
  }
  return active;
}

// Get normalized frequency buckets (0-1) for UI waveforms.
// Works as long as the audio graph exists, even if terrain reactivity is disabled.
export function getAudioSpectrum(bucketCount = 32) {
  if (!analyser || !frequencyData || bucketCount <= 0) {
    return [];
  }

  analyser.getByteFrequencyData(frequencyData);

  const count = Math.max(1, Math.floor(bucketCount));
  const binsPerBucket = Math.max(1, Math.floor(frequencyData.length / count));
  const spectrum = new Array(count).fill(0);

  for (let bucket = 0; bucket < count; bucket++) {
    const start = bucket * binsPerBucket;
    const end = (bucket === count - 1)
      ? frequencyData.length
      : Math.min(frequencyData.length, start + binsPerBucket);

    let sum = 0;
    const length = Math.max(1, end - start);
    for (let i = start; i < end; i++) {
      sum += frequencyData[i];
    }

    // Power curve makes small values visible on compact HUD waveforms.
    const normalized = sum / (length * 255);
    spectrum[bucket] = Math.sqrt(Math.max(0, Math.min(1, normalized)));
  }

  return spectrum;
}

// Destroy audio-reactive system and clean up
// Only call this when completely closing the audio player
export function destroyAudioReactive() {
  enabled = false;
  
  if (sourceNode) {
    try {
      sourceNode.disconnect();
    } catch (e) {
      // Ignore disconnect errors
    }
    sourceNode = null;
  }
  
  if (analyser) {
    try {
      analyser.disconnect();
    } catch (e) {
      // Ignore disconnect errors
    }
    analyser = null;
  }
  
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close().catch(() => {
      // Ignore close errors
    });
  }
  
  audioContext = null;
  frequencyData = null;
  smoothedEnergy = 0.0;
  
  console.log('Audio reactive system destroyed');
}

// Get debug info about the analyzer state
export function getAnalyzerDebugInfo() {
  return {
    enabled: enabled,
    hasAudioContext: audioContext !== null,
    audioContextState: audioContext?.state || 'none',
    hasAnalyser: analyser !== null,
    hasSourceNode: sourceNode !== null,
    hasFrequencyData: frequencyData !== null,
    frequencyBinCount: analyser?.frequencyBinCount || 0,
    smoothedEnergy: smoothedEnergy,
    isActive: isAudioReactiveActive()
  };
}
